import {nextTick} from 'vue';
import VueScrollTo from 'vue-scrollto';
import axios from 'axios';
import {globalActions} from '../store/global-actions';
import {store} from '../store/global-store';
import {formValidationMixin} from './mixins/component-mixins';
import {Field, Form, defineRule, configure} from 'vee-validate';
import {max, min} from '@vee-validate/rules';
import localizedValidationMessages from '../shared/validation-localization/localization';

defineRule('min', min);
defineRule('max', max);

const commentMixin = {
    props: {
        messages: {},
    },
    data() {
        return {
            showErrors: false,
        };
    },
    components: {
        ValidationField: Field,
        VeeValidationFormContext: Form,
    },
    emits: ['submit'],
    mixins: [formValidationMixin],
    computed: {
        validationModel() {
            return this.commentBody || {};
        },
    },
    methods: {
        showAjaxErrorModal(message) {
            globalActions.openInfoModal(this.messages.modals.errorTitle, message ? message : this.messages.modals.errorMessage);
        },
    },
};

const commentItem = {
    template: '#comment-item',
    props: {
        item: {},
        urls: {},
        messages: {},
    },
    mixins: [commentMixin],
    emits: ['create', 'delete', 'report'],
    data() {
        return {
            comment: this.item,
            isFormForEdit: false,
            commentBody: null,
            dateCheckingCounter: 1,
            isHighlighted: false,
        };
    },
    computed: {
        getLastCommentDate() {
            return this.dateCheckingCounter ? this.comment.lastUpdatedLocal : '';
        },
        commentSection() {
            return this.$parent.$parent;
        },
    },
    methods: {
        openEditForm(event) {
            this.commentBody = this.comment.body;
            this.isFormForEdit = true;
            nextTick(() => {
                this.focusTextarea(event);
            });
        },
        closeForm() {
            if (this.isFormForEdit) {
                this.commentBody = null;
            }
            this.isFormForEdit = false;
        },
        async submitForm() {
            const validator = this.$refs.validator;
            this.showErrors = true;
            const result = await validator.validate();
            if (result.valid) {
                if (this.isFormForEdit) {
                    this.editComment();
                } else {
                    this.replyComment();
                }
            }
        },
        focusTextarea() {
            const textarea = this.$el.querySelector('textarea');

            if (textarea) {
                textarea.style.height = `${textarea.scrollHeight}px`;
                textarea.focus();
            }

            this.$refs.dropdown.close();
        },
        editComment() {
            const comment = {
                body: this.commentBody,
                commentType: this.comment.commentType,
                targetId: this.comment.targetId,
                authorId: this.comment.authorId,
                author: this.comment.author,
                id: this.comment.id,
                replyToId: this.comment.replyToId,
                replyAuthorName: this.comment.replyAuthorName,
                created: this.comment.created,
            };

            if (comment.body !== this.comment.body) {
                this.trySaveEditedComment(comment);
            }
        },
        trySaveEditedComment(comment) {
            this.sendSaveEditedCommentRequest(comment);
            this.closeForm();
        },
        sendSaveEditedCommentRequest(comment) {
            axios.post(this.urls.edit, comment)
                .then((response) => {
                    this.comment = response.data;
                })
                .catch(this.showAjaxErrorModal);
        },
        replyComment() {
            const comment = {
                body: this.commentBody,
                commentType: this.comment.commentType,
                targetId: this.comment.targetId,
                replyToId: this.comment.id,
                replyAuthorName: this.comment.author.userName,
            };
            this.$emit(this.getFormAction(), comment);
            this.commentBody = null;
            this.closeForm();
        },
        getFormAction() {
            return this.isFormForEdit ? 'edit' : 'create';
        },
        startDatesChecking() {
            setInterval(() => {
                if (this.comment) {
                    this.dateCheckingCounter++;
                }
            }, 30000);
        },
        showDeleteModal() {
            this.$emit('delete', this.item.id);
        },
        showReportModal() {
            this.$emit('report', this.item.id);
        },
        highlight() {
            this.isHighlighted = true;
            setTimeout(() => this.isHighlighted = false, 2000);
        },
        async addReply(commentBody) {
            this.commentBody = commentBody;
            await this.submitForm();
        },
    },
    mounted() {
        this.startDatesChecking();
    },
};

const comments = {
    template: '#comments-template',
    props: {
        commentList: {},
        messages: {},
        urls: {},
        userInfo: {},
    },
    mixins: [commentMixin],
    data() {
        return {
            comments: this.commentList.comments,
            isAllowedToComment: this.commentList.isAllowedToComment,
            scrollContainer: this.commentList.scrollContainer,
            commentBody: null,
            currentComment: null,
            isUserAuthenticated: this.userInfo.isAuthenticated,
            isUserEmailConfirmed: this.userInfo.isEmailConfirmed,
            isDeleteModalOpened: false,
            modalTargetId: null,
        };
    },
    methods: {
        changeCommentsAvailability() {
            const fullUrl = `${this.urls.changeCommentsAvailability}&isAvailable=${!this.isAllowedToComment}`;
            axios.post(fullUrl)
                .then(() => {
                    this.isAllowedToComment = !this.isAllowedToComment;
                })
                .catch(this.showAjaxErrorModal);
        },
        getCommentUrlFragment(commentId) {
            return `comment${commentId}`;
        },
        async addNewComment() {
            const comment = {
                body: this.commentBody,
                commentType: this.commentList.commentType,
                targetId: this.commentList.targetId,
            };
            await this.createComment(comment);
        },
        async createComment(newItem) {
            this.currentComment = newItem;

            if (this._isUserAuthorized()) {
                this.showErrors = true;
                if (await this._validate()) {
                    this.sendCreateCommentRequest();
                }
            }
        },
        sendCreateCommentRequest() {
            axios.post(this.urls.create, this.currentComment)
                .then((response) => {
                    const savedComment = response.data;
                    this.comments.push(savedComment);

                    this.commentBody = null;
                    this.currentComment = null;
                    nextTick(() => {
                        this.scrollToComment(savedComment.id);
                        this.showErrors = false;
                    });
                })
                .catch(this.showAjaxErrorModal);
        },
        scrollToComment(commentId) {
            this.$scrollTo(`#${this.getCommentUrlFragment(commentId)}`, {container: this.scrollContainer});
            const comment = this.$refs[`comment-${commentId}`];
            if (comment[0]) {
                comment[0].highlight();
            }
        },
        showLoginRegisterModal() {
            globalActions.changePreselectRegistrationReason(globalActions.getCommentRegistrationPlace());
            localStorage.setItem('comment', JSON.stringify(this.currentComment));
            globalActions.openLoginModal();
        },
        deleteComment() {
            const deleteUrl = `${this.urls.delete}?commentId=${this.modalTargetId}`;
            axios.post(deleteUrl)
                .then((response) => {
                    const index = this.comments.findIndex((c) => c.id === this.modalTargetId);
                    this.comments[index].body = response.data;
                    this.comments[index].markedDeleted = true;
                })
                .catch(() => {
                    this.showAjaxErrorModal(this.messages.modals.emailNotConfirmedMessage);
                });
        },
        openDeleteModal(commentId) {
            this.isDeleteModalOpened = true;
            this.modalTargetId = commentId;
        },
        openReportModal(commentId, reportType) {
            if (this._isUserAuthorized()) {
                globalActions.openReportModal({targetId: commentId, reportType});
                this.modalTargetId = commentId;
            }
        },
        _isUserAuthorized() {
            let isAuthorized = true;

            if (!this.isUserAuthenticated) {
                isAuthorized = false;
                this.showLoginRegisterModal();
            } else if (!this.isUserEmailConfirmed) {
                isAuthorized = false;
                this.showAjaxErrorModal(this.messages.modals.emailNotConfirmedMessage);
            }

            return isAuthorized;
        },
        async _loadCommentFromStorage(comment) {
            if (comment.replyToId) {
                const parentComment = this.$refs[`comment-${comment.replyToId}`][0];
                await parentComment.addReply(comment.body);
            } else {
                this.commentBody = comment.body;
                await this.addNewComment();
            }
        },
        async _validate() {
            let isValid = false;
            const validator = this.$refs.validator;
            if (validator) {
                const result = await validator.validate();
                isValid = result.valid;
            }

            return isValid;
        },
    },
    watch: {
        comments() {
            globalActions.changeCommentsCount(this.comments.length);
        },
    },
    computed: {
        commentAvailabilityMessage() {
            return this.isAllowedToComment ? this.messages.commentsVisible : this.messages.commentsNotVisible;
        },
        changeAvailabilityButtonLabel() {
            return this.isAllowedToComment ? this.messages.hideComments : this.messages.showComments;
        },
        anyComments() {
            return this.comments.length > 0;
        },
        canHideComments() {
            return (this.isAllowedToComment);
        },
        sendCommentButtonId() {
            return this.commentList?.sendCommentButtonId;
        },
    },
    async mounted() {
        const comment = localStorage.getItem('comment');
        if (comment) {
            await this._loadCommentFromStorage(JSON.parse(comment));
            localStorage.removeItem('comment');
        }
    },
};

const commentLink = {
    template: '#comment-link',
    props: {
        linkClass: {default: ''},
        el: {required: true},
        duration: {default: 1000},
        container: {default: ''},
        // offset 1 is added to fix issue with scrolling not to top of the element
        offset: {default: 1},
        commentsNumber: {default: 0},
        hideCommentsNumber: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            commentsCount: this.commentsNumber,
        };
    },
    methods: {
        beforeScroll() {
            this.$emit('scroll', this);
        },
        _subscribeStore() {
            store.subscribe((mutation, _state) => {
                if (mutation.type === 'commentsCount') {
                    this.commentsCount = mutation.payload;
                }
            });
        },
    },
    mounted() {
        this._subscribeStore();
    },
};

function initCommentsComponents(app) {
    app.use(VueScrollTo, {
        duration: 1000,
        offset: -120,
    });

    configure({
        generateMessage: localizedValidationMessages(),
    });

    app.component('comment-item', commentItem);

    app.component('comments', comments);

    app.component('comment-link', commentLink);
};

export default initCommentsComponents;
